// B1385: the Functions page's server calls. `SHOW FUNCTIONS` lists the stored
// functions, and CREATE [OR REPLACE] / DROP FUNCTION and GRANT / REVOKE
// INVOKE change them. Every statement is the SQL of web/docs/sql.md
// § Functions and tickers, sent on the shared WS.
//
// `SHOW FUNCTIONS` answers `name`, `language`, `run_as` (NULL: the body runs
// as the caller) and `params` (a JSON array of the parameter names). B2172
// adds `param_types` (a JSON array of {name, type, null}), `returns` (text)
// and `typed` (false for a legacy function). It needs no authority, so it
// gives no body. `SHOW CREATE FUNCTION f` (B1386) gives
// the body, to a superuser or the run-as user of `f`. The page loads it to
// edit a function with CREATE OR REPLACE FUNCTION.
//
// The INVOKE rules are the `kind = function` rows of `SHOW GRANTS` (B1376).
// `SHOW GRANTS` is superuser-only, so a non-root session sees the server's
// `permission denied` in the page, never a fake empty list.

import { wsSql } from './socket.js';
import { PERMISSIONS, lit, quoteName, targetSql, parseTarget } from './grants-store.js';

// The body as a string literal. `$$…$$` keeps the body verbatim with no
// escape, but it ends at the first `$$` (`token.rs` `read_dollar_string`).
// So a body that holds `$$`, or that ends with `$` (`$$x$$$` ends at `x`),
// goes in `'…'` with each `'` doubled (`token.rs` `read_string`, B566).
export function bodyLiteral(body) {
  const s = String(body ?? '');
  if (!s.includes('$$') && !s.endsWith('$')) return `$$${s}$$`;
  return lit(s);
}

// B2172: the types of a parameter and of RETURNS (web/docs/sql.md § Functions
// and tickers). A CALL cannot carry BLOB, BYTES or COUNTER, so the server
// refuses them for a parameter. There is no VALUE and no ANY type.
export const PARAM_TYPES = ['INT', 'FLOAT', 'TEXT', 'BOOL', 'TIMESTAMP', 'VECTOR', 'ROW'];
const REFUSED_TYPES = ['BLOB', 'BYTES', 'COUNTER'];

// The canonical name of a type: `INT`, …, `VECTOR(n)` with n > 0, `VECTOR`
// (a list of any size, as a CREATE TABLE column), `ROW`.
export function typeSql(type) {
  const s = String(type ?? '').trim().toUpperCase();
  if (s === '') throw new Error('a type cannot be empty');
  const v = /^VECTOR\s*\(\s*(\d+)\s*\)$/.exec(s);
  if (v) {
    const n = Number(v[1]);
    if (!(n > 0)) throw new Error('VECTOR(n) needs n > 0');
    return `VECTOR(${n})`;
  }
  if (REFUSED_TYPES.includes(s)) throw new Error(`a CALL cannot carry ${s}: use another type`);
  if (s !== 'VECTOR' && /^VECTOR\b/.test(s)) throw new Error('write VECTOR(n) with n > 0, or VECTOR for any size');
  if (!PARAM_TYPES.includes(s)) throw new Error(`unknown type ${JSON.stringify(String(type).trim())}: use ${PARAM_TYPES.join(', ')}`);
  return s;
}

// One `name type [NULL | NOT NULL]` entry → {name, type, null}. NOT NULL is
// the default. A `[name]` loses its brackets.
function parseTyped(text) {
  const m = /^(\[[^\]]*\]|\S+)\s*(.*)$/.exec(text.trim());
  const name = m[1].startsWith('[') ? m[1].slice(1, -1) : m[1];
  let rest = m[2].trim();
  let nullable = false;
  const tail = /(?:^|\s+)(NOT\s+NULL|NULL)$/i.exec(rest);
  if (tail) {
    nullable = !/^NOT/i.test(tail[1]);
    rest = rest.slice(0, tail.index).trim();
  }
  if (rest === '') throw new Error(`parameter ${name} has no type: write a type, for example ${name} INT`);
  return { name, type: typeSql(rest), null: nullable };
}

// The entries of a comma list. A comma inside `( )` does not split.
function splitTop(text) {
  const out = [];
  let depth = 0;
  let cur = '';
  for (const c of text) {
    if (c === '(') depth++;
    if (c === ')') depth--;
    if (c === ',' && depth === 0) { out.push(cur); cur = ''; } else cur += c;
  }
  out.push(cur);
  return out.map((p) => p.trim());
}

// The typed parameters of a comma list (`x INT, y TEXT NULL`) →
// [{name, type, null}]. An empty list is no parameters; an empty entry and an
// entry without a type are refused.
export function splitParams(text) {
  const s = String(text ?? '').trim();
  if (s === '') return [];
  const list = splitTop(s);
  if (list.some((p) => p === '')) throw new Error('a parameter cannot be empty');
  return list.map(parseTyped);
}

// `[name] TYPE[ NULL]`. The name goes through `expect_ident`, which takes a
// `[name]`.
function typedSql(p, what) {
  if (p?.type == null || String(p.type).trim() === '') {
    throw new Error(`${what} ${p?.name} has no type: write a type, for example ${p?.name} INT`);
  }
  return `${quoteName(p.name)} ${typeSql(p.type)}${p.null ? ' NULL' : ''}`;
}

// The `returns` text of `SHOW FUNCTIONS` → {type, null} or {table: [{name,
// type, null}]}.
export function parseReturns(text) {
  const s = String(text ?? '').trim();
  if (s === '') throw new Error('a function needs a RETURNS type: write RETURNS <type> or RETURNS TABLE (...)');
  const t = /^TABLE\s*\(([\s\S]*)\)$/i.exec(s);
  if (t) {
    const cols = t[1].trim() === '' ? [] : splitTop(t[1]).map((c) => {
      if (c === '') throw new Error('a TABLE column cannot be empty');
      return parseTyped(c);
    });
    if (cols.length === 0) throw new Error('RETURNS TABLE needs at least one column');
    return { table: cols };
  }
  let rest = s;
  let nullable = false;
  const tail = /\s+(NOT\s+NULL|NULL)$/i.exec(rest);
  if (tail) {
    nullable = !/^NOT/i.test(tail[1]);
    rest = rest.slice(0, tail.index).trim();
  }
  return { type: typeSql(rest), null: nullable };
}

// The RETURNS clause of a text or of a {type, null} / {table} object.
export function returnsSql(ret) {
  const r = typeof ret === 'string' || ret == null ? parseReturns(ret) : ret;
  if (r.table) {
    if (r.table.length === 0) throw new Error('RETURNS TABLE needs at least one column');
    return `TABLE (${r.table.map((c) => typedSql(c, 'column')).join(', ')})`;
  }
  return `${typeSql(r.type)}${r.null ? ' NULL' : ''}`;
}

// `CREATE [OR REPLACE] FUNCTION <name>(<p> <type> [NULL], …) RETURNS <ret>
// LANGUAGE kd AS <body> [RUN AS '<user>']` (`parser.rs`
// `parse_create_function`). The server refuses a parameter without a type and
// a function without RETURNS (B2172).
export function createSql({ name, params = [], returns, body, runAs, orReplace = false }) {
  const fn = quoteName(name);
  const list = (Array.isArray(params) ? params : splitParams(params)).map((p) => typedSql(p, 'parameter'));
  const ret = returnsSql(returns);
  if (String(body ?? '').trim() === '') throw new Error('the body cannot be empty');
  const head = orReplace ? 'CREATE OR REPLACE FUNCTION' : 'CREATE FUNCTION';
  const run = runAs ? ` RUN AS ${lit(runAs)}` : '';
  return `${head} ${fn}(${list.join(', ')}) RETURNS ${ret} LANGUAGE kd AS ${bodyLiteral(body)}${run}`;
}

// The typed signature of a `parseFunctions` row, `(x INT, y TEXT NULL)
// RETURNS INT`, or null for a legacy (untyped) function.
export function signatureText(f) {
  if (f?.typed === false || f?.paramTypes == null) return null;
  const ps = f.paramTypes.map((p) => `${p.name} ${p.type}${p.null ? ' NULL' : ''}`);
  return `(${ps.join(', ')}) RETURNS ${f.returns ?? '?'}`;
}

export function dropSql(name) { return `DROP FUNCTION ${quoteName(name)}`; }
export function showCreateSql(name) { return `SHOW CREATE FUNCTION ${quoteName(name)}`; }

// A function has no rows, so its target is ANYONE, AUTHENTICATED or ROLE
// only (`parser.rs` `parse_function_grant_target`).
function fnTargetSql(target) {
  const k = target?.kind;
  if (k !== 'anyone' && k !== 'authenticated' && k !== 'role') {
    throw new Error(`a function takes ANYONE, AUTHENTICATED or ROLE, not ${JSON.stringify(k)}`);
  }
  return targetSql(target);
}

export function grantInvokeSql(name, target) {
  return `GRANT INVOKE ON FUNCTION ${quoteName(name)} TO ${fnTargetSql(target)}`;
}
export function revokeInvokeSql(name, target) {
  return `REVOKE INVOKE ON FUNCTION ${quoteName(name)} FROM ${fnTargetSql(target)}`;
}

function rowsByName(r) {
  const cols = r?.columns ?? [];
  return (r?.data ?? []).map((row) => {
    const o = {};
    cols.forEach((c, i) => { o[c] = row[i]; });
    return o;
  });
}

// The `params` value: a JSON array, or its text. An older server sends no
// `params` column, so a missing value is no parameters.
function paramList(v) {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'string' && v.trim() !== '') {
    const a = JSON.parse(v);
    if (Array.isArray(a)) return a.map(String);
  }
  return [];
}

// The `param_types` value (B2172): a JSON array of {name, type, null}, or its
// text. A legacy function and an older server send none: null.
function paramTypeList(v) {
  const a = typeof v === 'string' && v.trim() !== '' ? JSON.parse(v) : v;
  if (!Array.isArray(a)) return null;
  return a.map((p) => ({ name: String(p.name), type: String(p.type).toUpperCase(), null: p.null === true }));
}

// The `typed` value: a bool or its text. An older server sends none: null.
function boolOrNull(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v.toLowerCase() === 'true';
  return Boolean(v);
}

// `SHOW FUNCTIONS` → [{name, language, runAs, params, paramTypes, returns,
// typed}], read by column name. `params` holds the names. `typed` = false
// marks a legacy function: the server refuses its CALL until CREATE OR
// REPLACE FUNCTION gives it types.
export function parseFunctions(r) {
  return rowsByName(r).map((o) => ({
    name: o.name, language: o.language, runAs: o.run_as ?? null, params: paramList(o.params),
    paramTypes: paramTypeList(o.param_types), returns: o.returns ?? null, typed: boolOrNull(o.typed),
  }));
}

// `SHOW CREATE FUNCTION` → {name, params, runAs, source, statement}, read by
// column name. The server answers one row, or an error.
export function parseFunctionSource(r) {
  const o = rowsByName(r)[0];
  if (!o) throw new Error('SHOW CREATE FUNCTION answered no row');
  return {
    name: o.name,
    params: paramList(o.params),
    runAs: o.run_as ?? null,
    source: o.source ?? '',
    statement: o.statement ?? '',
  };
}

// The `kind = function` rows of `SHOW GRANTS` → [{fn, rights, grant, target}].
// The `collection` column holds the function name. An older server sends no
// `kind` column and has no function rules.
export function parseFunctionGrants(r) {
  return rowsByName(r).filter((o) => o.kind === 'function').map((o) => {
    const have = new Set(String(o.rights ?? '').split(',').map((s) => s.trim()).filter(Boolean));
    return {
      fn: o.collection,
      rights: PERMISSIONS.filter((p) => have.has(p)),
      grant: o.grant,
      target: parseTarget(o.grant),
    };
  });
}

// The REVOKE that removes this row, or the reason there is none. A rule of a
// dropped role shows as `PRINCIPAL <id>`, and REVOKE refuses an unknown role.
export function revokeInvokeFor(row) {
  try {
    return { sql: revokeInvokeSql(row.fn, row.target) };
  } catch (e) {
    if (row.target?.kind === 'principal') return { reason: `a rule of a dropped role (${row.target.id}): REVOKE refuses an unknown role` };
    return { reason: e.message };
  }
}

export async function listFunctions() { return parseFunctions(await wsSql('SHOW FUNCTIONS')); }
export async function loadFunctionSource(name) { return parseFunctionSource(await wsSql(showCreateSql(name))); }
export async function listFunctionGrants() { return parseFunctionGrants(await wsSql('SHOW GRANTS')); }

// Run the statements in order; the first refusal stops the list.
export async function runSql(list) {
  for (const sql of list) await wsSql(sql);
}
